import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig, ChartDataPoint } from "@/types";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { useChartHoverStore } from "@/stores/chart/chart-hover-store";
import { useComponentData } from "@/stores/editor/component-data-store";
import { normalizeChartRows } from "./chartDataUtils";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface PieChartProps {
  component: Component;
  isPreview?: boolean;
}

interface PieMouseMoveEvent {
  activePayload?: Array<{ payload?: Record<string, unknown> }>;
  activeTooltipIndex?: number | null;
}

interface PieClickPayload {
  payload?: Record<string, unknown>;
}

interface LegendValueObject {
  name?: string;
  key?: string;
}

const defaultData: ChartDataPoint[] = [
  { name: "Desktop", value: 400 },
  { name: "Mobile", value: 300 },
  { name: "Tablet", value: 200 },
  { name: "Other", value: 100 },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

function toOptionalNumber(value: string | number | undefined): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export const PieChart: React.FC<PieChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Get dataSource configuration
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    initialData: properties.data as Record<string, unknown>[] | undefined,
  });

  // Component data store (populated by fetch-schema-data interactions)
  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);

  // Loading/error state: merge store + query template
  const loading = storeLoading || qtLoading;
  const error = storeError || qtError;

  // Data priority: Query template > Store data > Static data > Default data
  const hasLiveQueryTemplateData = (hasQueryTemplate || !!templateId) && Array.isArray(queryTemplateData);
  const chartData = (hasLiveQueryTemplateData
    ? queryTemplateData
    : storeData.length > 0
      ? storeData
      : queryTemplateData || (properties.data && Array.isArray(properties.data)
      ? properties.data
      : defaultData)) as ChartDataPoint[];

  const normalizedChartData = normalizeChartRows(chartData as Record<string, unknown>[]) as ChartDataPoint[];

  // For PieChart: xAxisKey from query maps to nameKey, first measure maps to valueKey
  // Validate against actual data, auto-detect if needed
  const queryNameKey = hasQueryTemplate && dataSource?.queryDefinition ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  const autoNameKey = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(k => typeof normalizedChartData[0][k] === 'string')
    : null;
  // Validate explicit nameKey against actual data — fall through if it doesn't match
  const explicitNameKey = (properties.nameKey as string) || (properties.xAxisKey as string) || null;
  const validatedExplicitNameKey = explicitNameKey && normalizedChartData.length > 0 && explicitNameKey in normalizedChartData[0]
    ? explicitNameKey : null;
  const nameKey = validatedExplicitNameKey
    || (queryNameKey && normalizedChartData.length > 0 && queryNameKey in normalizedChartData[0] ? queryNameKey : null)
    || autoNameKey
    || queryNameKey
    || "name";

  const queryValueKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : null;
  const validatedPieValue = queryValueKeys?.[0] && normalizedChartData.length > 0 && queryValueKeys[0] in normalizedChartData[0]
    ? queryValueKeys[0] : null;
  const autoPieValue = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(key => key !== nameKey && typeof normalizedChartData[0][key] === 'number')
    : null;
  // Validate explicit valueKey against actual data — fall through if it doesn't match
  const explicitValueKey = typeof properties.valueKey === 'string'
    ? properties.valueKey
    : Array.isArray(properties.lineKeys)
      ? properties.lineKeys.find((key): key is string => typeof key === 'string') || null
      : null;
  const validatedExplicitValueKey = explicitValueKey && normalizedChartData.length > 0 && explicitValueKey in normalizedChartData[0]
    ? explicitValueKey : null;
  const valueKey = validatedExplicitValueKey
    || validatedPieValue
    || autoPieValue
    || queryValueKeys?.[0]
    || "value";

  // Get size configuration from component.size (no ResizeObserver needed)
  const sizeConfig = getChartSizeConfig(
    toOptionalNumber(size?.width),
    toOptionalNumber(size?.height)
  );

  // Get responsive configuration
  const title = properties.title as string;
  const description = properties.description as string;
  const hasTitle = title !== null && title !== undefined && title !== "";

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    hasTitle,
    !!description,
    undefined, // PieChart doesn't use margin
    properties.showLegend !== false,
    false // PieChart doesn't use grid
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [properties.colors, paletteChangeCounter]);

  const chartConfig: ChartConfig = {
    title,
    data: normalizedChartData,
    xAxisKey: nameKey,
    yAxisKey: valueKey,
    showLegend: responsiveConfig.showLegend,
    showTooltip: properties.showTooltip !== false,
    colors: resolvedColors,
    responsive: properties.responsive !== false,
    animation: properties.animation !== false,
  };

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  // Chart hover store handlers for compound chart integration
  const setHoverData = useChartHoverStore((state) => state.setHoverData);
  const clearHoverData = useChartHoverStore((state) => state.clearHoverData);

  const handleChartMouseMove = useCallback(
    (event: PieMouseMoveEvent) => {
      if (event.activePayload?.[0]?.payload) {
        setHoverData(
          component.id,
          event.activePayload[0].payload,
          event.activeTooltipIndex ?? null
        );
      }
    },
    [component.id, setHoverData]
  );

  const handleChartMouseLeave = useCallback(() => {
    clearHoverData(component.id);
  }, [component.id, clearHoverData]);

  const RADIAN = Math.PI / 180;

  interface LabelEntry {
    innerRadius: number;
    outerRadius: number;
    cx: number;
    cy: number;
    midAngle: number;
    percent: number;
  }

  const renderCustomizedLabel = (entry: LabelEntry) => {
    if (!(properties.showLabels as boolean)) return null;

    const radius =
      entry.innerRadius + (entry.outerRadius - entry.innerRadius) * 0.5;
    const x = entry.cx + radius * Math.cos(-entry.midAngle * RADIAN);
    const y = entry.cy + radius * Math.sin(-entry.midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > entry.cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={10}
        fontWeight="bold"
      >
        {`${(entry.percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 60 : sizeConfig.isSmall ? 100 : 120;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: (typeof properties.minHeight === 'string' || typeof properties.minHeight === 'number')
      ? properties.minHeight
      : `${dynamicMinimum + (hasTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {hasTitle && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="pie" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart
              style={{ userSelect: isPreview ? "auto" : "none" }}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
              <Pie
                data={chartConfig.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={properties.showLabels ? renderCustomizedLabel : false}
                outerRadius={(typeof properties.outerRadius === 'string' || typeof properties.outerRadius === 'number') ? properties.outerRadius : "80%"}
                innerRadius={(typeof properties.innerRadius === 'string' || typeof properties.innerRadius === 'number') ? properties.innerRadius : 0}
                fill="#8884d8"
                dataKey={chartConfig.yAxisKey}
                nameKey={chartConfig.xAxisKey}
                animationDuration={chartConfig.animation ? 300 : 0}
                onClick={(data: PieClickPayload, index: number) => {
                  const payload = data.payload ?? {};
                  handleChartClick(payload, payload[chartConfig.xAxisKey] as string | undefined, index);
                }}
              >
              {chartConfig.data.map((entry, index) => {
                const colorIndex = index % (chartConfig.colors?.length ?? defaultColors.length);
                const fillColor = chartConfig.colors?.[colorIndex] ?? defaultColors[colorIndex] ?? defaultColors[0];
                const cellNav = getNavigationForElement(undefined, entry[chartConfig.xAxisKey] as string | undefined);
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={fillColor}
                    cursor={isNavigable && cellNav ? 'pointer' : 'default'}
                  />
                );
              })}
            </Pie>
            {chartConfig.showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
              />
            )}
            {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{ fontSize: `${responsiveConfig.legendFontSize}px` }}
                formatter={(value: unknown) => {
                  // Handle both string values and dataset objects
                  if (typeof value === 'string') return value;
                  if (typeof value === 'object' && value !== null) {
                    const legendValue = value as LegendValueObject;
                    return legendValue.name || legendValue.key || String(value);
                  }
                  return String(value);
                }}
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
